import { useEffect, useMemo, useRef, useState } from 'react';
import { useDiagramStore } from '../../stores/diagramStore';
import { useUIStore } from '../../stores/uiStore';
import { useAIStore } from '../../stores/aiStore';
import { snapshotFromStore } from '../../stores/undoStore';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { DIAGRAM_KIT_IDS, getKit } from '../../utils/diagramKits';
import type { ThemePalette } from '../../types';

interface CommandPaletteProps {
  palette: ThemePalette;
  glass: React.CSSProperties;
  isViewOnly: boolean;
  windowSize: { width: number; height: number };
  onStartC4: () => void;
  onPasteMermaid: () => void;
  onGroupSelected: () => void;
}

interface PaletteItem {
  id: string;
  category: string;
  label: string;
  hint?: string;
  run: () => void;
}

export function CommandPalette({
  palette,
  glass,
  isViewOnly,
  windowSize,
  onStartC4,
  onPasteMermaid,
  onGroupSelected,
}: CommandPaletteProps) {
  const isOpen = useUIStore(s => s.isCommandPaletteOpen);
  const setOpen = useUIStore(s => s.setCommandPaletteOpen);
  const catalog = useDiagramStore(s => s.catalog);
  const pages = useDiagramStore(s => s.pages);
  const nodes = useDiagramStore(s => s.nodes);
  const selectedItems = useDiagramStore(s => s.selectedItems);
  const diagramType = useDiagramStore(s => s.diagramType);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setActive(0);
      return;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const items = useMemo<PaletteItem[]>(() => {
    const close = () => setOpen(false);
    const needle = query.trim().toLowerCase();
    const match = (text: string) => !needle || text.toLowerCase().includes(needle);
    const list: PaletteItem[] = [];

    if (!isViewOnly) {
      const actions: PaletteItem[] = [
        {
          id: 'action-c4',
          category: 'Actions',
          label: 'Start a C4 workspace',
          hint: 'Context, Containers, Sequence',
          run: () => { close(); onStartC4(); },
        },
        {
          id: 'action-mermaid',
          category: 'Actions',
          label: 'Paste Mermaid',
          hint: 'Import flowchart or sequence',
          run: () => { close(); onPasteMermaid(); },
        },
        {
          id: 'action-group',
          category: 'Actions',
          label: 'Group into architecture',
          hint: 'Ctrl+G · enter with Tab',
          run: () => { close(); onGroupSelected(); },
        },
        ...(diagramType !== 'sequence' ? [
          ...DIAGRAM_KIT_IDS.map(id => {
            const kit = getKit(id);
            return {
              id: `kit-${id}`,
              category: 'Kits',
              label: `Switch kit to ${kit.label}`,
              hint: kit.shortLabel,
              run: () => {
                close();
                snapshotFromStore();
                const st = useDiagramStore.getState();
                st.setPageKit(st.activePage, id);
              },
            };
          }),
          {
            id: 'action-text',
            category: 'Draw',
            label: 'Add typed text',
            hint: 'a+t · then click the canvas',
            run: () => {
              close();
              useUIStore.getState().setCanvasDrawTool('text');
            },
          },
          {
            id: 'action-arrow',
            category: 'Draw',
            label: 'Draw annotation arrow',
            hint: 'Then drag on the canvas',
            run: () => {
              close();
              useUIStore.getState().setCanvasDrawTool('arrow');
            },
          },
          {
            id: 'action-line',
            category: 'Draw',
            label: 'Draw a line',
            hint: 'Then drag on the canvas',
            run: () => {
              close();
              useUIStore.getState().setCanvasDrawTool('line');
            },
          },
          {
            id: 'action-frame',
            category: 'Draw',
            label: 'Draw annotation frame',
            hint: 'B · visual only, not architecture',
            run: () => {
              close();
              useUIStore.getState().setFrameDrawMode(true);
            },
          },
        ] as PaletteItem[] : []),
        {
          id: 'action-ai',
          category: 'Actions',
          label: 'Open AI chat',
          hint: 'Ctrl+Shift+A',
          run: () => {
            close();
            useAIStore.getState().setPanelOpen(true);
          },
        },
        {
          id: 'action-tour',
          category: 'Actions',
          label: 'Take the product tour',
          hint: 'Speech-bubble walkthrough',
          run: () => {
            close();
            useOnboardingStore.getState().startTutorial();
          },
        },
        {
          id: 'action-shortcuts',
          category: 'Actions',
          label: 'Keyboard shortcuts',
          hint: 'Full chord list',
          run: () => {
            close();
            useUIStore.getState().setChordModalOpen(true);
          },
        },
      ];
      actions.filter(item => match(item.label) || match(item.hint ?? '')).forEach(item => list.push(item));
    }

    pages.filter(page => match(page.name)).forEach(page => {
      list.push({
        id: `page-${page.id}`,
        category: 'Pages',
        label: page.name,
        hint: page.diagramType ?? 'flow',
        run: () => {
          close();
          useDiagramStore.getState().switchPage(page.id);
        },
      });
    });

    nodes.filter(node => node.type !== 'group_input' && node.type !== 'group_output' && match(node.label)).forEach(node => {
      list.push({
        id: `node-${node.id}`,
        category: 'Nodes',
        label: node.label,
        hint: node.type,
        run: () => {
          close();
          const st = useDiagramStore.getState();
          st.selectItem(node.id);
          const scale = st.viewport.scale;
          st.updateViewport({
            x: windowSize.width / 2 - (node.position.x + node.size.width / 2) * scale,
            y: windowSize.height / 2 - (node.position.y + node.size.height / 2) * scale,
          });
        },
      });
    });

    catalog.filter(entry => match(entry.name) || match(entry.type)).forEach(entry => {
      list.push({
        id: `cat-${entry.id}`,
        category: 'Catalog',
        label: entry.name,
        hint: entry.type + (entry.c4Level ? ` · ${entry.c4Level}` : ''),
        run: () => {
          close();
          const st = useDiagramStore.getState();
          const existingNode = st.nodes.find(node => node.catalogId === entry.id);
          const existingParticipant = st.participants.find(p => p.catalogId === entry.id);
          if (existingNode) {
            st.selectItem(existingNode.id);
            const scale = st.viewport.scale;
            st.updateViewport({
              x: windowSize.width / 2 - (existingNode.position.x + existingNode.size.width / 2) * scale,
              y: windowSize.height / 2 - (existingNode.position.y + existingNode.size.height / 2) * scale,
            });
            return;
          }
          if (existingParticipant) {
            st.selectItem(existingParticipant.id);
            return;
          }
          if (isViewOnly) return;
          snapshotFromStore();
          const createdId = st.placeCatalogInstance(entry.id);
          if (createdId) st.selectItem(createdId);
        },
      });
    });

    return list;
  }, [
    catalog,
    diagramType,
    isViewOnly,
    nodes,
    onGroupSelected,
    onPasteMermaid,
    onStartC4,
    pages,
    query,
    setOpen,
    windowSize.height,
    windowSize.width,
  ]);

  useEffect(() => {
    setActive(0);
  }, [query, isOpen]);

  if (!isOpen) return null;

  const runActive = () => {
    const item = items[active];
    if (item) item.run();
  };

  return (
    <div
      className="fixed inset-0 z-[600] flex items-start justify-center pt-[18vh]"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/45" />
      <div
        className="relative z-10 w-[520px] max-h-[60vh] flex flex-col rounded-2xl border shadow-2xl overflow-hidden"
        style={glass}
        onClick={e => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive(i => Math.min(items.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive(i => Math.max(0, i - 1));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              runActive();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
            }
          }}
          placeholder="Jump to page, node, catalog… or run an action"
          className="w-full bg-transparent px-4 py-3 text-sm outline-none border-b"
          style={{ color: palette.panelText, borderColor: palette.panelBorder }}
        />
        <div className="flex-1 overflow-y-auto py-1">
          {items.length === 0 && (
            <div className="px-4 py-6 text-xs" style={{ color: palette.panelSubText }}>
              No matches
            </div>
          )}
          {items.map((item, index) => (
            <button
              key={item.id}
              className="w-full text-left px-4 py-2 text-sm flex items-center justify-between gap-3"
              style={{
                color: palette.panelText,
                background: index === active ? 'rgba(99,102,241,0.16)' : 'transparent',
              }}
              onMouseEnter={() => setActive(index)}
              onClick={item.run}
            >
              <span className="truncate">
                <span className="text-[10px] uppercase tracking-wider mr-2" style={{ color: palette.panelSubText }}>
                  {item.category}
                </span>
                {item.label}
              </span>
              {item.hint && (
                <span className="text-[10px] flex-shrink-0" style={{ color: palette.panelSubText }}>{item.hint}</span>
              )}
            </button>
          ))}
        </div>
        {!isViewOnly && selectedItems.length >= 2 && (
          <div className="px-4 py-2 text-[10px] border-t" style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}>
            {selectedItems.length} selected — Group into architecture groups components (Ctrl+G), not annotation frames
          </div>
        )}
      </div>
    </div>
  );
}
